import { useState, type FormEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { Boxes } from 'lucide-react'
import { catalogApi, type CatalogComponent, type CatalogProduct } from '@/api/catalogApi'
import { ConfirmDialog } from '@/components/common/ConfirmDialog'
import { EmptyState, errorMessage } from '@/components/common/States'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { FormError, StatusPill, TextAction, tdClass, thClass } from '@/components/admin/catalog/CatalogShared'
import { useCatalogMutation } from '@/hooks/useCatalog'
import { keyFromName, validateComponent } from '@/lib/catalogForms'

/** Components of a product: inline add, rename, (de)activate; delete only while unreferenced. */
export function ComponentsPanel({ product }: { product: CatalogProduct }) {
  const { t } = useTranslation()
  const [name, setName] = useState('')
  const [key, setKey] = useState('')
  const [keyTouched, setKeyTouched] = useState(false)
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [renaming, setRenaming] = useState<{ id: string; name: string } | null>(null)
  const [deleting, setDeleting] = useState<CatalogComponent | null>(null)

  const add = useCatalogMutation((input: { key: string; name: string }) => catalogApi.addComponent(product.id, input))
  const update = useCatalogMutation(({ id, ...input }: { id: string; name?: string; active?: boolean }) =>
    catalogApi.updateComponent(product.id, id, input),
  )
  const remove = useCatalogMutation((id: string) => catalogApi.removeComponent(product.id, id))

  function submit(e: FormEvent) {
    e.preventDefault()
    const found = validateComponent({ key, name }, product.components.map((c) => c.key)) as Record<string, string>
    setErrors(found)
    if (Object.keys(found).length) return
    add.mutate(
      { key, name: name.trim() },
      {
        onSuccess: () => {
          setName('')
          setKey('')
          setKeyTouched(false)
        },
      },
    )
  }

  return (
    <div className="flex flex-col gap-4">
      <form className="flex items-start gap-2" onSubmit={submit} noValidate aria-label={t('catalog.components.add')}>
        <div className="flex w-72 flex-col gap-1">
          <Input
            aria-label={t('catalog.products.name')}
            placeholder={t('catalog.components.namePlaceholder')}
            value={name}
            onChange={(e) => {
              setName(e.target.value)
              if (!keyTouched) setKey(keyFromName(e.target.value, 64))
            }}
          />
          {errors.name ? <p className="text-label text-status-escalated-fg">{t(errors.name)}</p> : null}
        </div>
        <div className="flex w-56 flex-col gap-1">
          <Input
            aria-label={t('catalog.products.key')}
            className="font-mono"
            placeholder={t('catalog.components.keyPlaceholder')}
            value={key}
            onChange={(e) => {
              setKeyTouched(true)
              setKey(e.target.value)
            }}
          />
          {errors.key ? <p className="text-label text-status-escalated-fg">{t(errors.key)}</p> : null}
        </div>
        <Button type="submit" disabled={add.isPending}>
          {t('catalog.components.add')}
        </Button>
      </form>
      {add.error ? <FormError error={add.error} /> : null}
      {update.error ? <FormError error={update.error} /> : null}

      {product.components.length === 0 ? (
        <EmptyState compact icon={Boxes} message={t('catalog.components.empty')} />
      ) : (
        <table className="w-full table-fixed border-collapse">
          <thead className="bg-surface-subtle">
            <tr>
              <th className={thClass}>{t('catalog.components.cols.name')}</th>
              <th className={`${thClass} w-[220px]`}>{t('catalog.components.cols.key')}</th>
              <th className={`${thClass} w-24`}>{t('catalog.components.cols.tickets')}</th>
              <th className={`${thClass} w-28`}>{t('catalog.components.cols.status')}</th>
              <th className={`${thClass} w-[220px]`} />
            </tr>
          </thead>
          <tbody>
            {product.components.map((c) => {
              const tickets = c._count?.tickets ?? 0
              const isRenaming = renaming?.id === c.id
              return (
                <tr key={c.id} className="border-b border-line">
                  <td className={`${tdClass} font-medium text-ink-strong`}>
                    {isRenaming ? (
                      <form
                        className="flex gap-2"
                        onSubmit={(e) => {
                          e.preventDefault()
                          if (renaming.name.trim())
                            update.mutate({ id: c.id, name: renaming.name.trim() }, { onSuccess: () => setRenaming(null) })
                        }}
                      >
                        <Input
                          autoFocus
                          aria-label={t('catalog.rename')}
                          className="h-8"
                          value={renaming.name}
                          onChange={(e) => setRenaming({ id: c.id, name: e.target.value })}
                        />
                        <Button type="submit" size="sm" disabled={update.isPending}>
                          {t('common.save')}
                        </Button>
                        <Button type="button" size="sm" variant="outline" onClick={() => setRenaming(null)}>
                          {t('common.cancel')}
                        </Button>
                      </form>
                    ) : (
                      c.name
                    )}
                  </td>
                  <td className={`${tdClass} font-mono`}>{c.key}</td>
                  <td className={`${tdClass} font-mono`}>{tickets}</td>
                  <td className={tdClass}>
                    <StatusPill status={c.active ? 'active' : 'inactive'} />
                  </td>
                  <td className={tdClass}>
                    <div className="flex justify-end gap-3">
                      <TextAction onClick={() => setRenaming({ id: c.id, name: c.name })}>{t('catalog.rename')}</TextAction>
                      <TextAction onClick={() => update.mutate({ id: c.id, active: !c.active })} disabled={update.isPending}>
                        {c.active ? t('catalog.deactivate') : t('catalog.activate')}
                      </TextAction>
                      <TextAction danger disabled={tickets > 0} onClick={() => setDeleting(c)}>
                        {t('common.delete')}
                      </TextAction>
                    </div>
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      )}

      <ConfirmDialog
        open={deleting !== null}
        destructive
        title={t('catalog.components.deleteTitle', { name: deleting?.name ?? '' })}
        body={t('catalog.components.deleteBody')}
        confirmLabel={t('common.delete')}
        pending={remove.isPending}
        error={remove.error ? errorMessage(remove.error) : null}
        onOpenChange={(o) => {
          if (!o) {
            setDeleting(null)
            remove.reset()
          }
        }}
        onConfirm={() => deleting && remove.mutate(deleting.id, { onSuccess: () => setDeleting(null) })}
      />
    </div>
  )
}
